import { useCallback, useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { io } from 'socket.io-client';
import moment from 'moment';
import { api } from '../api/api';

export const useSocketIo = () => {
  const socket = useRef(null);
  const request = useRef({});
  const [result, setResult] = useState({});
  const [history, setHistory] = useState([]);
  const [status, setStatus] = useState('disconnected');

  const contract_id = useSelector(state => state.contracts.activeContract?.id);

  const handleChangeResult = useCallback((data) => {
    setResult(data);
  }, []);

  useEffect(() => {
    if (contract_id && socket.current === null) {
      const token = localStorage.getItem('token') || '';
      socket.current = io(`${api().data.replace('/api', '')}`, {
        path: '/api/mqtt',
        transports: ['websocket'],
        auth: {
          username: token,
          password: '',
          contract_id
        },
        reconnection: true,
        reconnectionAttempts: 50,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000
      });
    }
  }, [contract_id]);

  useEffect(() => {
    if (socket.current) {
      socket.current.on('connect', () => setStatus('connected'));
      socket.current.on('disconnect', () => setStatus('disconnected'));
      socket.current.on('error', (error) => {
        console.error(error);
        setStatus('error');
      });
      socket.current.on('reconnect', (attemptNumber) => {
        setStatus(`reconnect ${attemptNumber}`);
      });
      socket.current.on('reconnect_failed', () => {
        setStatus('reconnect_failed');
      });
    }
  }, [socket, contract_id]);

  useEffect(() => {
    return () => {
      if (socket.current) {
        socket.current.disconnect();
        socket.current = null;
      }
    };
  }, []);

  const send = useCallback(async (topic, data) => {
    request.current = { topic, data };
    socket.current?.emit(topic, data);
  }, []);

  const subscribe = useCallback((topics = [], callback) => {
    topics.forEach(topic => {
      socket.current?.off(topic);
      socket.current?.on(topic, lastJsonMessage => {
        const data = {
          command: request.current?.data?.command,
          topic,
          typeof: typeof lastJsonMessage,
          date: moment().format('YYYY-MM-DD HH:mm:ss'),
          result: JSON.parse(JSON.stringify(lastJsonMessage)),
        };
        setHistory(prev => [...prev, { ...data, request: request.current }]);
        setResult(data);
      });
    });
    if (typeof callback === 'function') callback();
  }, []);

  const unSubscribe = useCallback((topics = [], callback) => {
    topics.forEach(topic => {
      socket.current?.off(topic, callback);
    });
  }, []);

  return {
    result,
    status,
    history,
    send,
    subscribe,
    unSubscribe,
    handleChangeResult
  };
};
